import React, {useEffect, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useImmer} from 'use-immer';
import {cn} from '@/utils';

import {SelectBoxComponent} from '@/components';
import {matrixSelector, pageOptionsSelector, setQuantity} from '../../slices/matrixSlice';
import {appSelector} from '../../slices/appSlice';
import PrimaryButton from "../../../../components/molecules/Buttons";

const ID = 'quantity';

const Price = () => {
  const {
    isFetching,
    customQuantities,
    prices,
    config,
    productId,
    defaultConfig,
    optionsOverwrite,
    priceListStyle,
    customShape: { dpoData },
    config: { show_roll_label_options, is_custom_shape },
    AveryCustom: { client, customVat }
  } = useSelector(matrixSelector);
  const defaultPageOptions = useSelector(pageOptionsSelector());
  const rollLabelPageOptions = useSelector(pageOptionsSelector(true, 'roll'));
  const selectBoxRef = useRef();
  const {priceDisplay} = useSelector(appSelector);
  const priceSequence = priceDisplay.split('_');
  const dispatch = useDispatch();
  const { selection_quantity } = prices;

  const [componentState, updateComponentState] = useImmer({
    isExpanded: false,
    loadingIndex: null,
  });

  const toggleExpanded = () => {
    updateComponentState((draft) => {
      draft.isExpanded = !componentState.isExpanded;
    });
  };

  const closeExpanded = () => {
    updateComponentState((draft) => {
      draft.isExpanded = false;
    });
  };

  const handleItemClick = ({parentId, id, is_selected}) => {
    if (!is_selected && !isFetching) {
      updateComponentState((draft) => {
        draft.loadingIndex = `${parentId}_${id}`;
      });

      dispatch(
        setQuantity({
          variables: {
            productId,
            client,
            customVat,
            defaultConfig,
            optionsOverwrite: {...optionsOverwrite, [parentId]: id},
            customQuantities,
          },
        })
      );
    } else if (is_selected) {
      toggleExpanded();
    }
  };

  const handleCustomQuantity = (customQuantity) => {
    const hasQuantity = prices.values.find(
      (price) => price.quantity === parseInt(customQuantity)
    );
    if (hasQuantity) {
      if (hasQuantity.is_selected) {
        updateComponentState((draft) => {
          draft.isExpanded = false;
        });
      } else {
        handleItemClick({parentId: prices.id, ...hasQuantity});
      }
    } else {
      dispatch(
        setQuantity({
          variables: {
            productId,
            client,
            customVat,
            optionsOverwrite: {
              ...optionsOverwrite,
              quantity: `custom_qty_${customQuantity}`,
            },
            customQuantities: [...customQuantities, customQuantity],
          },
        })
      );
    }
  };

  useEffect(() => {
    if (!isFetching) {
      updateComponentState((draft) => {
        draft.loadingIndex = null;
        draft.isExpanded = false;
      });
    }
  }, [isFetching]);

  return (
    <If condition={!is_custom_shape || (is_custom_shape && dpoData)}>
      <SelectBoxComponent
        id={ID}
        ref={selectBoxRef}
        testid = {process.env.RENDER_TESTIDS ? "priceselect" : undefined}
        headline={
          Drupal.t('Choose your @item:', {'@item': prices.label})
        }
        showIcon={prices.values.length > 1}
        onClick={toggleExpanded}
        onClickOutside={closeExpanded}
        onKeyDown={(e) => {
          // Handle Enter, Space and Escape key
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            closeExpanded();
          }
        }}
        isExpanded={componentState.isExpanded}
        isDisabled={isFetching}
        disabledClassName="opacity-40 bg-neutral-light cursor-wait"
        buttonDataAttributes={{'data-quantity': selection_quantity}}
        DropdownContent={
          <DropdownContent
            parentId={prices.id}
            prices={prices}
            onItemClick={handleItemClick}
            loadingIndex={componentState.loadingIndex}
            stateConfig={config}
            handleCustomQuantity={handleCustomQuantity}
            isFetching={isFetching}
            priceSequence={priceSequence}
            priceListStyle={priceListStyle}
            selectBoxRef={selectBoxRef}
            closeExpanded={closeExpanded}
          />
        }
      >
        <div className="flex items-center text-base">
          <div className="flex flex-col flex-1 basis-1/2 text-left text-sm laptop:basis-1/3 desktop:text-base">
            <span>
              {prices.selection_quantity.toLocaleString()}{' '}
              {prices.selection_quantity === 1
                ? prices.labels.singular_price_label
                : prices.labels.plural_price_label}
            </span>
            <If condition={prices.selection_number_of_single_products > 0}>
              <span className="font-medium text-xs">
                ({prices.selection_number_of_single_products.toLocaleString()}{' '}
                {prices.selection_number_of_single_products === 1
                  ? prices.labels.singular_product_label
                  : prices.labels.plural_product_label})
              </span>
            </If>
          </div>
          <If
            condition={!prices.selection_price[priceSequence[0]].formatted_special_price_total || priceListStyle !== 'savings'}
          >
            <div className={cn("flex-1 basis-1/3 text-center text-2xs hidden laptop:block laptop:text-xs",
              priceListStyle === 'savings' && prices.values.findIndex(price => price.quantity === prices.selection_quantity)
                ? "text-[color:var(--theme-color-secondary)] font-bold"
                : "font-medium",
              prices.selection_discount_percentage ? "text-[color:var(--theme-color-secondary)]" : ""
            )}>
              {prices.selection_price[priceSequence[0]].info_text}
            </div>
          </If>
          <div className="flex-1 text-right whitespace-nowrap basis-1/2 laptop:basis-1/3">
            <If condition={priceSequence.length === 1}>
              <div className={
                prices.selection_price[priceSequence[0]].formatted_special_price_total
                  ? "font-semibold leading-none line-through text-2xs desktop:text-xs"
                  : "font-bold text-lg desktop:text-2xl text-brand-1"
              }>
                {prices.selection_price[priceSequence[0]].formatted_price_total}
              </div>
              <If condition={prices.selection_price[priceSequence[0]].formatted_special_price_total}>
                <div className="font-bold no-underline text-[color:var(--theme-color-secondary)] text-lg desktop:text-2xl">
                  {prices.selection_price[priceSequence[0]].formatted_special_price_total}
                </div>
              </If>
            </If>
            <If condition={priceSequence.length > 1}>
              <If condition={prices.selection_price[priceSequence[0]].formatted_special_price_total}>
                <div className="flex items-center justify-end gap-4xs">
                  <div className="font-semibold leading-none line-through text-2xs desktop:text-xs">
                    {prices.selection_price[priceSequence[0]].formatted_price_total}
                  </div>
                  <div className="font-bold text-[color:var(--theme-color-secondary)] text-base tablet:text-lg desktop:text-xl">
                    {prices.selection_price[priceSequence[0]].formatted_special_price_total}
                  </div>
                </div>
                <div className="font-medium leading-none text-2xs desktop:text-xs">
                  {prices.selection_price[priceSequence[1]].formatted_special_price_total}{' '}
                  {priceSequence[1] === 'net'
                    ? Drupal.t('excl. VAT')
                    : Drupal.t('incl. VAT')}
                </div>
              </If>
              <If condition={!prices.selection_price[priceSequence[0]].formatted_special_price_total}>
                <div
                  className="font-bold text-brand-1 text-base tablet:text-lg desktop:text-xl">
                  {prices.selection_price[priceSequence[0]].formatted_price_total}
                </div>
                <div className="font-medium leading-none text-2xs desktop:text-xs">
                  {prices.selection_price[priceSequence[1]].formatted_price_total}{' '}
                  {priceSequence[1] === 'net'
                    ? Drupal.t('excl. VAT')
                    : Drupal.t('incl. VAT')}
                </div>
              </If>
            </If>
          </div>
        </div>
      </SelectBoxComponent>
    </If>
  );
};

const DropdownContent = ({
                           parentId,
                           selectBoxRef,
                           prices,
                           onItemClick,
                           loadingIndex,
                           stateConfig,
                           handleCustomQuantity,
                           isFetching,
                           priceSequence,
                           priceListStyle,
                           closeExpanded
                         }) => {
  const {values} = prices;
  const customQuantityField = useRef();
  const [quantityDisabled, setQuantityDisabled] = useImmer(true);
  const [showRangeOverflowText, setShowRangeOverflowText] = useImmer(false);

  const btnAttributes = {}
  if (isFetching || quantityDisabled) {
    btnAttributes.disabled = true;
  }

  const handleCustomQuantityChange = (e) => {
    if (customQuantityField.current) {
      const validity = customQuantityField.current.checkValidity();
      const {rangeOverflow} = customQuantityField.current.validity;

      setQuantityDisabled(!validity);
      if (!shouldShowRangOverflowText(rangeOverflow)) {
        setShowRangeOverflowText(false);
        customQuantityField.current.reportValidity();
      } else {
        setShowRangeOverflowText(true);
      }
    }
  };

  const shouldShowRangOverflowText = (rangeOverflow) => {
    const textDifferent = Drupal.t("The quantity must be less than @max pieces.", { "@max": 1 }) !== "The quantity must be less than 1 pieces.";
    return textDifferent === true && rangeOverflow === true;
  };

  const handleKeyDown = (e) => {
    const validity = customQuantityField.current.checkValidity();

    if ((e.code === 'Enter' || e.code === 'NumpadEnter') && validity) {
      handleCustomQuantity(e.target.value);
    }
  };

  useEffect(() => {
    if (isFetching && loadingIndex === null) {
      customQuantityField.current.value = '';
      customQuantityField.current.blur();
    }
  }, [isFetching]);

  return (
    <div
      className="absolute z-overlay mt-3xs w-full bg-white shadow border border-neutral-darker rounded overflow-hidden"
    >
      <ul
        role="listbox"
        aria-labelledby={`listbox-${ID}-label`}
        className={cn(
          "dropdown-content relative overflow-auto m-0 max-h-full",
          isFetching ? "opacity-60" : ""
        )}
      >
        <If condition={stateConfig.custom_quantity?.is_enabled === true}>
          <li className="bg-neutral-medium px-xl py-lg border-b border-b-neutral-darker">
            <div className="w-full tablet:grid tablet:grid-cols-2 laptop:grid-cols-3 items-center justify-items-center gap-lg">
              <div className="laptop:col-span-2 flex-col w-full">
                <label htmlFor="custom-qty-label" className="block font-semibold mb-4xs text-sm text-center tablet:text-left desktop:text-base">
                  {Drupal.t('Individual quantity:')}
                </label>
                <input
                  id="custom-qty"
                  name="custom-qty"
                  aria-describedby="custom-qty-descr"
                  type="number"
                  className="relative w-full"
                  placeholder={
                    !isFetching || (isFetching && loadingIndex !== null)
                      ? Drupal.t('Quantity') + ' ' + prices.labels.plural_price_label
                      : ''
                  }
                  ref={customQuantityField}
                  onChange={handleCustomQuantityChange}
                  onKeyDown={handleKeyDown}
                  step={stateConfig.custom_quantity.step}
                  min={stateConfig.custom_quantity.min}
                  max={stateConfig.custom_quantity.max}
                  required
                />
                <div id="custom-qty-descr" className="font-medium text-2xs text-center mt-5xs tablet:text-left desktop:text-xs">
                  Min: {stateConfig.custom_quantity.min} | Max:{' '}
                  {stateConfig.custom_quantity.max.toLocaleString()}
                </div>
              </div>
              <div className="w-full mt-2xs tablet:mt-3xs">
                <PrimaryButton
                  type="button"
                  onClick={() =>
                    handleCustomQuantity(customQuantityField.current.value)
                  }
                  {...btnAttributes}
                >
                  {Drupal.t('Select')}
                </PrimaryButton>
              </div>
            </div>
            <If condition={showRangeOverflowText}>
              <div className='flex items-center mt-xs'>
                <ft-icon name="info"></ft-icon>
                <div className="ml-xs">
                  {Drupal.t("The quantity must be less than @max pieces.", {"@max": 1})}
                </div>
              </div>
            </If>
          </li>
        </If>
        {values.map(
          (
            {quantity, number_of_single_products, price, is_selected, id, discount_percentage},
            i
          ) => (
            <li
              key={i}
              id={`listbox-${ID}-${i}`}
              role="option"
              aria-selected={is_selected}
              data-quantity={quantity}
              tabIndex="0"
              className={cn(
                'relative flex flex-row items-center min-h-[60px] py-2xs pl-lg pr-5xl border-b border-b-neutral-darker cursor-pointer',
                'desktop:pr-6xl hover:bg-neutral-light focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-1',
                is_selected || loadingIndex === `${parentId}_${id}`
                  ? 'bg-neutral-light ring-2 ring-inset ring-brand-1 border-b-0'
                  : 'bg-white'
              )}
              onClick={() => onItemClick({parentId, id, is_selected})}
              onKeyDown={(e) => {
                // Handle Enter, Space and Escape key
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onItemClick({parentId, id, is_selected}, i);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  closeExpanded();
                }
              }}
              onBlur={({ relatedTarget }) => {
                if (relatedTarget && selectBoxRef.current && !selectBoxRef.current.contains(relatedTarget)) {
                  closeExpanded();
                }
              }}
            >
              <div className="flex items-center flex-grow">
                <div className="flex flex-col flex-1 basis-1/2 laptop:basis-1/3">
                    <span className="font-bold text-sm desktop:text-base">
                      {quantity.toLocaleString()}{' '}
                      {quantity === 1
                        ? prices.labels.singular_price_label
                        : prices.labels.plural_price_label}
                    </span>
                  <If condition={number_of_single_products > 0}>
                      <span className="font-medium text-xs">
                        ({number_of_single_products.toLocaleString()}{' '}
                        {number_of_single_products === 1
                          ? prices.labels.singular_product_label
                          : prices.labels.plural_product_label})
                      </span>
                  </If>
                </div>
                <If condition={!price[priceSequence[0]].formatted_special_price_total || (!discount_percentage || priceListStyle !== 'savings')}>
                  <div className={cn(
                    "flex-1 basis-1/3 text-center text-2xs hidden laptop:block desktop:text-xs",
                    priceListStyle === 'savings' ? "text-[color:var(--theme-color-secondary)] font-bold" : "font-medium",
                    discount_percentage ? "text-[color:var(--theme-color-secondary)]" : ""
                  )}>
                    {price[priceSequence[0]].info_text}
                  </div>
                </If>
                <div className="flex-1 ml-3xs text-right whitespace-nowrap basis-1/2 laptop:basis-1/3">
                  <If condition={priceSequence.length === 1}>
                    {price[priceSequence[0]].formatted_special_price_total ?
                      discount_percentage ?
                        <ft-badge value={prices.selection_discount_percentage} discount="">
                          <div className="font-semibold leading-none line-through mr-4xs text-2xs desktop:text-xs">
                            {price[priceSequence[0]].formatted_price_total}
                          </div>
                          <div className="font-bold no-underline text-[color:var(--theme-color-secondary)] text-base tablet:text-lg desktop:text-xl">
                            {price[priceSequence[0]].formatted_special_price_total}
                          </div>
                        </ft-badge>
                        :
                        <>
                          <div className="font-semibold line-through text-2xs desktop:text-xs">
                            {price[priceSequence[0]].formatted_price_total}
                          </div>
                          <div
                            className="font-bold no-underline text-[color:var(--theme-color-secondary)] text-base tablet:text-lg desktop:text-xl">
                            {price[priceSequence[0]].formatted_special_price_total}
                          </div>
                        </>
                      :
                      <div className="font-bold text-brand-1 text-base tablet:text-lg desktop:text-xl">
                        {price[priceSequence[0]].formatted_price_total}
                      </div>
                    }
                  </If>
                  <If condition={priceSequence.length > 1}>
                    <If condition={price[priceSequence[0]].formatted_special_price_total}>
                      <ft-badge value={discount_percentage} discount="">
                        <div className="font-semibold leading-none line-through mr-4xs text-2xs desktop:text-xs">
                          {price[priceSequence[0]].formatted_price_total}
                        </div>
                        <div
                          className="font-bold text-[color:var(--theme-color-secondary)] text-base tablet:text-lg desktop:text-xl">
                          {price[priceSequence[0]].formatted_special_price_total}
                        </div>
                      </ft-badge>
                      <div className="font-medium leading-none text-2xs desktop:text-xs">
                        {price[priceSequence[1]].formatted_special_price_total}{' '}
                        {priceSequence[1] === 'net'
                          ? Drupal.t('excl. VAT')
                          : Drupal.t('incl. VAT')}
                      </div>
                    </If>
                    <If condition={!price[priceSequence[0]].formatted_special_price_total}>
                      <div
                        className="font-bold text-base tablet:text-lg desktop:text-xl text-brand-1">
                        {price[priceSequence[0]].formatted_price_total}
                      </div>
                      <div className="font-medium leading-none text-2xs desktop:text-xs">
                        {price[priceSequence[1]].formatted_price_total}{' '}
                        {priceSequence[1] === 'net'
                          ? Drupal.t('excl. VAT')
                          : Drupal.t('incl. VAT')}
                      </div>
                    </If>
                  </If>
                </div>
              </div>
            </li>
          )
        )}
        <If condition={isFetching}>
          <li
            className="absolute top-0 left-0 pointer-events-none cursor-wait flex w-full h-full justify-center items-center"
          >
            <ft-spinner size="large"></ft-spinner>
          </li>
        </If>
      </ul>
    </div>
  );
};

export default Price;
